import { PublicClientApplication } from '@azure/msal-browser';

/**
 * AttendanceService
 *
 * Staff roster: pulled live from Azure AD (Microsoft Graph) — no manual list to maintain.
 * Absence flags (set by the Schedule app): read from the SharePoint "Staff" list, matched by email.
 * Clients: read from the SharePoint "Clients" list (clients don't have AD accounts).
 * Check-in/out records: written to a dedicated "BuildingAttendance" SharePoint list.
 *
 * Azure AD app registration requirements:
 *   • API permission: Microsoft Graph → Delegated → User.Read.All  (grant admin consent)
 *   • API permission: SharePoint → Delegated → AllSites.Read (already present)
 *
 * SharePoint list required: "BuildingAttendance"
 * Columns:
 *   Title         – Single line of text  (person's full name)
 *   PersonId      – Number               (0 for visitors; Azure AD Object ID stored in AadObjectId)
 *   AadObjectId   – Single line of text  (Azure AD user objectId for staff)
 *   PersonType    – Choice               (Staff | Client | Parent | Provider | Visitor | Other)
 *   AttendanceDate– Date Only
 *   CheckInTime   – Date and Time
 *   CheckOutTime  – Date and Time        (can be blank)
 *   IsCheckedIn   – Yes/No
 *   Purpose       – Single line of text
 *   HostName      – Single line of text
 *   Organization  – Single line of text
 *
 * Optional list: "FireDrills"
 * Columns:
 *   Title         – Single line of text  (e.g. "2025-04-15 10:32 AM")
 *   DrillDate     – Date Only
 *   StartTime     – Date and Time
 *   EndTime       – Date and Time
 *   TotalCount    – Number
 *   AccountedCount– Number
 *   UnaccountedNames – Multi-line text   (newline-delimited)
 *   Notes         – Multi-line text
 */
export class AttendanceService {
  constructor(config) {
    this.siteUrl = config.siteUrl;
    this.clientId = config.clientId;
    this.tenantId = config.tenantId;
    this.redirectUri = config.redirectUri;

    this.msalConfig = {
      auth: {
        clientId: this.clientId,
        authority: `https://login.microsoftonline.com/${this.tenantId}`,
        redirectUri: this.redirectUri,
      },
      cache: { cacheLocation: 'localStorage', storeAuthStateInCookie: false },
    };

    this.msalInstance = new PublicClientApplication(this.msalConfig);
    this.isInitialized = false;
    this.accessToken = null;
    this.tokenExpiry = null;

    // SharePoint token request
    this.loginRequest = {
      scopes: ['https://evokebehavioralhealthcom.sharepoint.com/.default'],
    };

    // Graph token request — used separately for reading Azure AD group members
    this.graphRequest = {
      scopes: ['https://graph.microsoft.com/GroupMember.Read.All'],
    };

    // Optional: restrict Graph user query to a specific Azure AD group.
    // Set this to the Object ID of your "Staff" or "Employees" group in Azure AD,
    // or leave null to pull all enabled accounts in the tenant.
    // Find the group ID: Azure Portal → Azure AD → Groups → [group name] → Overview → Object ID
    this.staffGroupId = config.staffGroupId || null;
  }

  // ─── Auth ────────────────────────────────────────────────────────────────

  async initializeMSAL() {
    if (this.isInitialized) return;
    await this.msalInstance.initialize();
    this.isInitialized = true;
  }

  async login() {
    await this.initializeMSAL();
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        const result = await this.msalInstance.acquireTokenSilent({
          ...this.loginRequest, account: accounts[0],
        });
        this.accessToken = result.accessToken;
        this.tokenExpiry = result.expiresOn;
        return result.account;
      } catch {
        // Fall through to interactive
      }
    }
    const result = await this.msalInstance.loginPopup(this.loginRequest);
    this.accessToken = result.accessToken;
    this.tokenExpiry = result.expiresOn;
    return result.account;
  }

  async handleRedirect() {
    await this.initializeMSAL();
    try {
      const result = await this.msalInstance.handleRedirectPromise();
      if (result) {
        this.accessToken = result.accessToken;
        this.tokenExpiry = result.expiresOn;
        return result.account;
      }
    } catch (e) {
      console.error('Redirect handling error:', e);
    }
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        const result = await this.msalInstance.acquireTokenSilent({
          ...this.loginRequest, account: accounts[0],
        });
        this.accessToken = result.accessToken;
        this.tokenExpiry = result.expiresOn;
        return accounts[0];
      } catch { /* not signed in */ }
    }
    return null;
  }

  logout() {
    this.accessToken = null;
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      this.msalInstance.logoutPopup({ account: accounts[0] });
    }
  }

  isAuthenticated() {
    return !!this.accessToken && (!this.tokenExpiry || new Date() < this.tokenExpiry);
  }

  async ensureToken() {
    if (this.isAuthenticated()) return this.accessToken;
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) throw new Error('Not authenticated');
    const result = await this.msalInstance.acquireTokenSilent({
      ...this.loginRequest, account: accounts[0],
    });
    this.accessToken = result.accessToken;
    this.tokenExpiry = result.expiresOn;
    return this.accessToken;
  }

  async getHeaders(includeWrite = false) {
    const token = await this.ensureToken();
    const headers = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json;odata=verbose',
    };
    if (includeWrite) {
      headers['Content-Type'] = 'application/json;odata=verbose';
      headers['X-RequestDigest'] = await this.getRequestDigest();
    }
    return headers;
  }

  async getRequestDigest() {
    if (this.digest && this.digestExpiry && new Date() < this.digestExpiry) {
      return this.digest;
    }
    const token = await this.ensureToken();
    const resp = await fetch(`${this.siteUrl}/_api/contextinfo`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json;odata=verbose',
      },
    });
    const data = await resp.json();
    this.digest = data.d.GetContextWebInformation.FormDigestValue;
    this.digestExpiry = new Date(Date.now() + 25 * 60 * 1000); // 25 min
    return this.digest;
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  /**
   * Resolve a SharePoint user ID (integer) from an email address.
   * Required for setting Person fields via the REST API.
   * Returns null if the user isn't found in the site's user list.
   */
  async getSpUserIdByEmail(email) {
    try {
      const headers = await this.getHeaders();
      const url = `${this.siteUrl}/_api/web/siteusers?$filter=Email eq '${email}'&$select=Id,Email`;
      const data = await this.apiFetch(url, { headers });
      const results = data.d?.results || [];
      return results.length > 0 ? results[0].Id : null;
    } catch {
      return null;
    }
  }

  async apiFetch(url, options = {}) {
    const resp = await fetch(url, options);
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`API error ${resp.status}: ${text.slice(0, 200)}`);
    }
    return resp.json();
  }

  // ─── Graph token (separate resource from SharePoint) ─────────────────────

  async getGraphToken() {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) throw new Error('Not authenticated');
    try {
      const result = await this.msalInstance.acquireTokenSilent({
        ...this.graphRequest,
        account: accounts[0],
        forceRefresh: true, // Always get a fresh token so new permissions take effect immediately
      });
      return result.accessToken;
    } catch {
      // Silent failed — try interactive popup
      const result = await this.msalInstance.acquireTokenPopup({
        ...this.graphRequest, account: accounts[0],
      });
      return result.accessToken;
    }
  }

  // ─── Load staff from Azure AD (Microsoft Graph) ───────────────────────────

  /**
   * Returns all enabled user accounts, optionally filtered to a specific
   * Azure AD group (set staffGroupId in config to restrict to staff only).
   *
   * Each user is then cross-referenced against the SharePoint "Staff" list
   * to pick up today's absence flags set by the Schedule app.
   */
  async loadStaff() {
    const [adUsers, spAbsenceFlags] = await Promise.all([
      this._loadUsersFromGraph(),
      this._loadAbsenceFlagsFromSharePoint(),
    ]);

    // Index SharePoint absence flags by email (lower-cased) for fast lookup
    const flagsByEmail = {};
    spAbsenceFlags.forEach(item => {
      if (item.email) flagsByEmail[item.email.toLowerCase()] = item;
    });

    return adUsers.map(user => {
      const flags = flagsByEmail[user.email.toLowerCase()] || {};
      return {
        // Identity from Azure AD
        id: user.id,           // Azure AD object ID (string)
        name: user.name,
        email: user.email,
        role: user.jobTitle || '',
        department: user.department || '',
        // Absence flags from SharePoint (default false if not in SP list yet)
        absentAM: flags.absentAM ?? false,
        absentPM: flags.absentPM ?? false,
        absentFullDay: flags.absentFullDay ?? false,
        absentAMArrivalTime: flags.absentAMArrivalTime || '',
        absentPMDepartureTime: flags.absentPMDepartureTime || '',
        outOfSessionFullDay: flags.outOfSessionFullDay ?? false,
      };
    });
  }

  async _loadUsersFromGraph() {
    const token = await this.getGraphToken();
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    let url;
    if (this.staffGroupId) {
      // /microsoft.graph.user type cast returns only user objects (not groups/service
      // principals) and unlocks user-specific properties like jobTitle, department.
      url = `https://graph.microsoft.com/v1.0/groups/${this.staffGroupId}/members/microsoft.graph.user` +
        `?$select=id,displayName,jobTitle,department,mail,userPrincipalName` +
        `&$top=999`;
    } else {
      // Pull all enabled accounts in the tenant (excludes disabled/deleted)
      url = `https://graph.microsoft.com/v1.0/users` +
        `?$select=id,displayName,jobTitle,department,mail,userPrincipalName,accountEnabled` +
        `&$filter=accountEnabled eq true` +
        `&$top=999`;
    }

    // Page through results if there are more than 999 users
    const users = [];
    let nextLink = url;
    while (nextLink) {
      const data = await this.apiFetch(nextLink, { headers });
      const page = data.value || [];
      page.forEach(u => {
        // Skip guest accounts (external users invited to the tenant)
        if (u.userPrincipalName && u.userPrincipalName.includes('#EXT#')) return;
        // Skip accounts with no display name
        if (!u.displayName) return;
        users.push({
          id: u.id,
          name: u.displayName,
          email: (u.mail || u.userPrincipalName || '').toLowerCase(),
          jobTitle: u.jobTitle || '',
          department: u.department || '',
        });
      });
      nextLink = data['@odata.nextLink'] || null;
    }

    return users.sort((a, b) => a.name.localeCompare(b.name));
  }

  async _loadAbsenceFlagsFromSharePoint() {
    // Read today's absence flags from the Schedule app's Staff list.
    // StaffAccount is a Person field — we expand it to get the user's email,
    // which is then used to match against Azure AD accounts.
    try {
      const headers = await this.getHeaders();
      const extendedSelect = 'Id,Title,StaffPerson/EMail,AbsentAM,AbsentPM,AbsentFullDay,AbsentAMArrivalTime,AbsentPMDepartureTime,OutOfSessionFullDay';
      const legacySelect = 'Id,Title,StaffPerson/EMail,AbsentAM,AbsentPM,AbsentFullDay,OutOfSessionFullDay';
      const makeUrl = (selectFields) => `${this.siteUrl}/_api/web/lists/getbytitle('Staff')/items?` +
        `$select=${selectFields}&` +
        `$expand=StaffPerson&` +
        `$top=500`;

      let data;
      try {
        data = await this.apiFetch(makeUrl(extendedSelect), { headers });
      } catch (err) {
        if (/AbsentAMArrivalTime|AbsentPMDepartureTime|does not exist/i.test(err.message || '')) {
          data = await this.apiFetch(makeUrl(legacySelect), { headers });
        } else {
          throw err;
        }
      }

      return (data.d?.results || []).map(item => ({
        spId: item.Id,
        name: item.Title || '',
        email: (item.StaffPerson?.EMail || '').toLowerCase(),
        absentAM: item.AbsentAM ?? false,
        absentPM: item.AbsentPM ?? false,
        absentFullDay: item.AbsentFullDay ?? false,
        absentAMArrivalTime: item.AbsentAMArrivalTime || '',
        absentPMDepartureTime: item.AbsentPMDepartureTime || '',
        outOfSessionFullDay: item.OutOfSessionFullDay ?? false,
      }));
    } catch {
      // If the SP list doesn't exist or errors, absence flags just default to false
      return [];
    }
  }

  // ─── Read Clients (same list as Schedule app, read-only) ─────────────────

  async loadClients() {
    const headers = await this.getHeaders();
    const url = `${this.siteUrl}/_api/web/lists/getbytitle('Clients')/items?` +
      `$select=Id,Title,Program,IsActive,AbsentAM,AbsentPM,AbsentFullDay&` +
      `$filter=IsActive eq 1&$orderby=Title&$top=500`;

    const data = await this.apiFetch(url, { headers });
    return (data.d?.results || []).map(item => ({
      id: item.Id,
      name: item.Title || '',
      program: item.Program || '',
      isActive: item.IsActive ?? true,
      absentAM: item.AbsentAM ?? false,
      absentPM: item.AbsentPM ?? false,
      absentFullDay: item.AbsentFullDay ?? false,
    }));
  }

  // ─── BuildingAttendance list ──────────────────────────────────────────────

  /**
   * Load all attendance records for a specific date (YYYY-MM-DD).
   */
  async loadAttendanceForDate(dateStr) {
    const headers = await this.getHeaders();
    const start = `${dateStr}T00:00:00Z`;
    const end   = `${dateStr}T23:59:59Z`;

    // Expand both Person fields to get display name + email
    const url = `${this.siteUrl}/_api/web/lists/getbytitle('BuildingAttendance')/items?` +
      `$select=Id,Title,PersonType,AttendanceDate,CheckInTime,CheckOutTime,IsCheckedIn,Purpose,Organization,` +
      `LinkedPerson/Id,LinkedPerson/Title,LinkedPerson/EMail,` +
      `Host/Id,Host/Title,Host/EMail&` +
      `$expand=LinkedPerson,Host&` +
      `$filter=AttendanceDate ge datetime'${start}' and AttendanceDate le datetime'${end}'&` +
      `$orderby=CheckInTime&$top=500`;

    try {
      const data = await this.apiFetch(url, { headers });
      return (data.d?.results || []).map(r => this._mapRecord(r));
    } catch (err) {
      if (err.message.includes('404')) {
        console.warn('BuildingAttendance list not found — create it in SharePoint.');
        return [];
      }
      throw err;
    }
  }

  /**
   * Create a new check-in record.
   * For staff: linkedPersonId is their SharePoint user ID (integer) so SharePoint
   * can resolve the Person field. Visitors leave it null.
   */
  async createCheckIn(record) {
    // For staff, look up their SharePoint user ID by email so the LinkedPerson
    // field is populated — this is what lets us reload the record with the
    // correct personId and merge it back into the expected roster.
    let spUserId = record.spUserId || null;
    if (!spUserId && record.email && record.personType === 'staff') {
      spUserId = await this.getSpUserIdByEmail(record.email);
    }

    const headers = await this.getHeaders(true);

    const body = JSON.stringify({
      __metadata: { type: 'SP.Data.BuildingAttendanceListItem' },
      Title:          record.personName,
      LinkedPersonId: spUserId || null,
      HostId:         record.hostSpUserId || null,
      PersonType:     record.personType,
      AttendanceDate: `${record.date}T00:00:00Z`,
      CheckInTime:    record.checkInTime,
      CheckOutTime:   null,
      IsCheckedIn:    true,
      Purpose:        record.purpose || '',
      Organization:   record.organization || '',
    });

    const url = `${this.siteUrl}/_api/web/lists/getbytitle('BuildingAttendance')/items`;
    const data = await this.apiFetch(url, { method: 'POST', headers, body });
    return this._mapRecord(data.d);
  }

  /**
   * Mark an existing record as checked out.
   * spItemId is the SharePoint list item Id.
   */
  async updateCheckOut(spItemId, checkOutTime) {
    const headers = await this.getHeaders(true);
    headers['X-HTTP-Method'] = 'MERGE';
    headers['If-Match'] = '*';

    const body = JSON.stringify({
      __metadata: { type: "SP.Data.BuildingAttendanceListItem" },
      CheckOutTime: checkOutTime,
      IsCheckedIn: false,
    });

    const url = `${this.siteUrl}/_api/web/lists/getbytitle('BuildingAttendance')/items(${spItemId})`;
    await fetch(url, { method: 'POST', headers, body });
    // MERGE returns 204 No Content
  }

  // ─── FireDrills list ──────────────────────────────────────────────────────

  /**
   * Save a completed fire drill record.
   */
  async saveFireDrill({ date, startTime, endTime, totalCount, accountedCount, unaccountedNames, notes }) {
    const headers = await this.getHeaders(true);
    const body = JSON.stringify({
      __metadata: { type: "SP.Data.FireDrillsListItem" },
      Title: `${date} ${new Date(startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`,
      DrillDate: `${date}T00:00:00Z`,
      StartTime: startTime,
      EndTime: endTime,
      TotalCount: totalCount,
      AccountedCount: accountedCount,
      UnaccountedNames: (unaccountedNames || []).join('\n'),
      Notes: notes || '',
    });

    const url = `${this.siteUrl}/_api/web/lists/getbytitle('FireDrills')/items`;
    try {
      await this.apiFetch(url, { method: 'POST', headers, body });
    } catch (err) {
      // FireDrills list is optional — don't crash if it doesn't exist yet
      if (err.message.includes('404')) {
        console.warn('FireDrills list not found — drill result not saved to SharePoint.');
      } else {
        throw err;
      }
    }
  }

  // ─── Map helpers ──────────────────────────────────────────────────────────

  _mapRecord(item) {
    // LinkedPerson is filled for staff; null for visitors/clients
    const linkedEmail = item.LinkedPerson?.EMail || '';
    const hostName    = item.Host?.Title || '';

    return {
      spId:       item.Id,
      // Use Azure AD email as the stable local ID for staff records so they
      // merge correctly with the Graph-sourced roster in BuildingView
      personId:   linkedEmail ? `aad-email-${linkedEmail.toLowerCase()}` : `visitor-${item.Id}`,
      spUserId:   item.LinkedPerson?.Id || null,
      personName: item.Title || '',
      personType: (item.PersonType || 'visitor').toLowerCase(),
      email:      linkedEmail.toLowerCase(),
      date:       item.AttendanceDate ? item.AttendanceDate.slice(0, 10) : '',
      checkInTime:  item.CheckInTime  || null,
      checkOutTime: item.CheckOutTime || null,
      isCheckedIn:  item.IsCheckedIn ?? false,
      purpose:      item.Purpose      || '',
      host:         hostName,
      hostEmail:    item.Host?.EMail  || '',
      organization: item.Organization || '',
    };
  }
}
